import { combineEpics } from 'redux-observable';
import { defer, from, merge, Observable, of, race } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { logoutAsync } from '@bolteu/redux-session';

import { requestAccountInfoAsyncAction, selectSelectedProvider } from 'rdx/account';
import { GlobalAppActions } from 'rdx/actions';
import { AppServices } from 'rdx/middleware';
import { GlobalAppState } from 'rdx/state';
import { setDeviceType } from 'rdx/theme/actions';
import { selectDeviceType } from 'rdx/theme/selectors';

import { requestTargetingParametersAsyncAction } from './actions';
import { sameTargetingSession, selectTargetingSessionContext } from './selectors';

type TargetingDependencies = {
  apiClient: {
    deliveryProviderPortal: Pick<
      AppServices['apiClient']['deliveryProviderPortal'],
      'getTargetingValuesForProvider'
    >;
  };
};

const requestTargetingDeviceTypeDependencies = (
  action$: Observable<GlobalAppActions>,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf([requestAccountInfoAsyncAction.success, setDeviceType])),
    switchMap((action) => {
      const safeRefresh = isActionOf(setDeviceType)(action);
      return of(requestTargetingParametersAsyncAction.request({ safeRefresh }));
    }),
  );
};

const requestTargetingParametersEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient }: TargetingDependencies,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(requestTargetingParametersAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      const requestId = state.targetingParameters.requestId;
      const selectedProvider = selectSelectedProvider(state);
      const deviceType = selectDeviceType(state);
      const sessionContext = selectTargetingSessionContext(state);

      if (sessionContext === null || !selectedProvider?.providerId) {
        return of(
          requestTargetingParametersAsyncAction.failure(
            new Error('Missing current account session or selected provider id'),
            requestId,
          ),
        );
      }

      const cancelled = merge(
        action$.pipe(filter(isActionOf([logoutAsync.request, logoutAsync.success]))),
        state$.pipe(
          filter(
            (nextState) =>
              !sameTargetingSession(sessionContext, selectTargetingSessionContext(nextState)),
          ),
        ),
      ).pipe(
        take(1),
        map(() => requestTargetingParametersAsyncAction.cancel(requestId)),
      );
      const response = defer(() =>
        from(
          apiClient.deliveryProviderPortal.getTargetingValuesForProvider({
            provider_id: selectedProvider.providerId,
            city_id: selectedProvider.cityId,
            country_code: selectedProvider.countryCode,
            signup_source: selectedProvider.signup_source,
            provider_status: selectedProvider.lifecycle_status,
            device_type: deviceType,
          }),
        ),
      ).pipe(
        map((value) =>
          requestTargetingParametersAsyncAction.success(value, {
            requestId,
            session: sessionContext,
          }),
        ),
        catchError((error: Error) =>
          of(requestTargetingParametersAsyncAction.failure(error, requestId)),
        ),
      );
      return race(cancelled, response);
    }),
  );
};

const requestTargetingSessionChanges = (
  _action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
): Observable<GlobalAppActions> =>
  state$.pipe(
    map(selectTargetingSessionContext),
    distinctUntilChanged(sameTargetingSession),
    pairwise(),
    filter(
      ([previous, current]) =>
        previous !== null &&
        current !== null &&
        previous.accountId === current.accountId &&
        previous.sessionId !== current.sessionId,
    ),
    map(() => requestTargetingParametersAsyncAction.request({ safeRefresh: true })),
  );

const requestTargetingAfterFailedLogout = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
): Observable<GlobalAppActions> =>
  action$.pipe(
    filter(isActionOf(logoutAsync.failure)),
    withLatestFrom(state$),
    filter(
      ([, state]) =>
        selectTargetingSessionContext(state) !== null &&
        !!selectSelectedProvider(state)?.providerId,
    ),
    map(() => requestTargetingParametersAsyncAction.request({ safeRefresh: true })),
  );

export const targetingParametersEpic = combineEpics(
  requestTargetingDeviceTypeDependencies,
  requestTargetingSessionChanges,
  requestTargetingAfterFailedLogout,
  requestTargetingParametersEpic,
);
